import { useState, useEffect } from 'react';
import { api } from '../services/api';

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [allAdGroups, setAllAdGroups] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [availableFilter, setAvailableFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProfile, setNewProfile] = useState({ profile_id: '', name: '', color: '' });
  const [showCreateByUserModal, setShowCreateByUserModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFilter, setUserFilter] = useState('');
  const [createByUserStep, setCreateByUserStep] = useState(1);

  useEffect(() => {
    Promise.all([
      api.listProfiles(),
      api.listAdGroups(),
      api.listUsers()
    ])
      .then(([profilesData, groupsData, usersData]) => {
        setProfiles(profilesData.result || []);
        setAllAdGroups(groupsData.result || []);
        setUsers(usersData.result || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const openGroupModal = (profile) => {
    setEditingProfile(profile);
    setSelectedGroups(profile.ad_groups || []);
    setAvailableFilter('');
    setSelectedFilter('');
    setShowGroupModal(true);
  };

  const moveToSelected = (group) => {
    if (!selectedGroups.find(g => g.full_ad_name === group.full_ad_name)) {
      setSelectedGroups([...selectedGroups, group]);
    }
  };

  const removeFromSelected = (group) => {
    setSelectedGroups(selectedGroups.filter(g => g.full_ad_name !== group.full_ad_name));
  };

  const saveGroups = () => {
    const updatedProfile = {
      ...editingProfile,
      ad_groups: selectedGroups
    };
    
    api.updateProfile(editingProfile.profile_id, updatedProfile)
      .then(() => {
        setProfiles(profiles.map(p => 
          p.profile_id === editingProfile.profile_id ? updatedProfile : p
        ));
        if (selectedProfile?.profile_id === editingProfile.profile_id) {
          setSelectedProfile(updatedProfile);
        }
        setShowGroupModal(false);
      })
      .catch(err => setError(err.message));
  };

  const confirmDelete = (profile, e) => {
    e.stopPropagation();
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  };

  const deleteProfile = () => {
    api.deleteProfile(profileToDelete.profile_id)
      .then(() => {
        setProfiles(profiles.filter(p => p.profile_id !== profileToDelete.profile_id));
        if (selectedProfile?.profile_id === profileToDelete.profile_id) {
          setSelectedProfile(null);
        }
        setShowDeleteModal(false);
        setProfileToDelete(null);
      })
      .catch(err => setError(err.message));
  };

  const openCreateModal = () => {
    setNewProfile({ profile_id: '', name: '', color: '#3b82f6' });
    setSelectedGroups([]);
    setAvailableFilter('');
    setSelectedFilter('');
    setShowCreateModal(true);
  };

  const openCreateByUserModal = () => {
    setNewProfile({ profile_id: '', name: '', color: '#3b82f6' });
    setSelectedUser(null);
    setUserFilter('');
    setSelectedGroups([]);
    setCreateByUserStep(1);
    setShowCreateByUserModal(true);
  };

  const selectUserForProfile = (user) => {
    api.getUser(user.username)
      .then(data => {
        const userData = data.result;
        const userGroups = userData.groups || [];
        setSelectedUser(userData);
        setSelectedGroups(userGroups.map(g => ({
          name: g.name,
          description: g.description,
          full_ad_name: g.full_name
        })));
        setCreateByUserStep(2);
      })
      .catch(err => setError(err.message));
  };

  const createProfileFromUser = () => {
    if (!newProfile.profile_id || !newProfile.name) {
      setError('Profile ID and Name are required');
      return;
    }
    
    const profileData = {
      ...newProfile,
      ad_groups: selectedGroups
    };
    
    api.createProfile(profileData)
      .then((data) => {
        setProfiles([...profiles, data.result]);
        setShowCreateByUserModal(false);
      })
      .catch(err => setError(err.message));
  };

  const createProfile = () => {
    if (!newProfile.profile_id || !newProfile.name) {
      setError('Profile ID and Name are required');
      return;
    }
    
    const profileData = {
      ...newProfile,
      ad_groups: selectedGroups
    };
    
    api.createProfile(profileData)
      .then((data) => {
        setProfiles([...profiles, data.result]);
        setShowCreateModal(false);
      })
      .catch(err => setError(err.message));
  };

  const handleProfileClick = (profile_id) => {
    api.getProfile(profile_id)
      .then(data => setSelectedProfile(data.result))
      .catch(err => setError(err.message));
  };

  if (loading) return <div className="loading">Loading profiles...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
          <h2 style={{margin: 0}}>Profiles</h2>
          <div style={{display: 'flex', gap: '0.5rem'}}>
            <button className="btn-primary" onClick={openCreateModal}>
              Create Profile
            </button>
            <button className="btn-primary" onClick={openCreateByUserModal}>
              Create Profile by User
            </button>
          </div>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Profile ID</th>
              <th>Name</th>
              <th>Color</th>
              <th>AD Groups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map(profile => (
              <tr key={profile.profile_id} onClick={() => handleProfileClick(profile.profile_id)} style={{cursor: 'pointer'}}>
                <td>{profile.profile_id}</td>
                <td>{profile.name}</td>
                <td>
                  <div className="color-indicator" style={{backgroundColor: profile.color || '#6b7280'}}></div>
                </td>
                <td>{profile.ad_groups?.length || 0}</td>
                <td>
                  <button 
                    className="btn-danger btn-small" 
                    onClick={(e) => confirmDelete(profile, e)}
                    title="Delete profile"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          }
          </tbody>
        </table>
      </div>

      {selectedProfile && (
        <div className="card">
          <h2>Profile Details</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Profile ID</span>
              <span className="detail-value">{selectedProfile.profile_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Name</span>
              <span className="detail-value">{selectedProfile.name}</span>
            </div>
          </div>
          <div style={{marginTop: '1.5rem'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
              <span className="detail-label">AD Groups</span>
              <button className="btn-primary" onClick={() => openGroupModal(selectedProfile)}>
                Edit Groups
              </button>
            </div>
            {selectedProfile.ad_groups && selectedProfile.ad_groups.length > 0 ? (
              <div className="group-list">
                {selectedProfile.ad_groups.map((group, idx) => (
                  <div key={idx} className="group-item">
                    <div className="group-name">{group.name}</div>
                    {group.description && <div className="group-desc">{group.description}</div>}
                    <div style={{fontSize: '0.8rem', color: '#95a5a6', marginTop: '0.25rem'}}>{group.full_ad_name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted">No groups assigned</div>
            )}
          </div>
        </div>
      )}

      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Groups - {editingProfile?.name}</h2>
              <button className="modal-close" onClick={() => setShowGroupModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label className="form-label">Color</label>
                <input
                  type="color"
                  value={editingProfile?.color || '#3b82f6'}
                  onChange={(e) => setEditingProfile({...editingProfile, color: e.target.value})}
                  className="color-input"
                />
              </div>
              <div className="group-selector">
                <div className="group-column">
                  <h3>Available Groups</h3>
                  <input
                    type="text"
                    placeholder="Filter available groups..."
                    value={availableFilter}
                    onChange={(e) => setAvailableFilter(e.target.value)}
                    className="group-filter-input"
                  />
                  <div className="group-list-box">
                    {allAdGroups
                      .filter(group => !selectedGroups.find(sg => sg.full_ad_name === group.full_ad_name))
                      .filter(group => group.name.toLowerCase().includes(availableFilter.toLowerCase()))
                      .map((group, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => moveToSelected(group)}>
                          <div className="group-name">{group.name}</div>
                          <div className="group-desc">{group.full_ad_name}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
                <div className="group-arrows">
                  <div>↔</div>
                </div>
                <div className="group-column">
                  <h3>Selected Groups</h3>
                  <input
                    type="text"
                    placeholder="Filter selected groups..."
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="group-filter-input"
                  />
                  <div className="group-list-box">
                    {selectedGroups
                      .filter(group => group.name.toLowerCase().includes(selectedFilter.toLowerCase()))
                      .map((group, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => removeFromSelected(group)}>
                          <div className="group-name">{group.name}</div>
                          <div className="group-desc">{group.full_ad_name}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={saveGroups}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content group-modal create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Profile</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-fields">
                <div className="form-field">
                  <label className="form-label">Profile ID</label>
                  <input
                    type="text"
                    value={newProfile.profile_id}
                    onChange={(e) => setNewProfile({...newProfile, profile_id: e.target.value})}
                    className="form-input"
                    placeholder="Enter profile ID"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                    className="form-input"
                    placeholder="Enter profile name"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Color</label>
                  <input
                    type="color"
                    value={newProfile.color}
                    onChange={(e) => setNewProfile({...newProfile, color: e.target.value})}
                    className="color-input"
                  />
                </div>
              </div>
              
              <div className="group-selector">
                <div className="group-column">
                  <h3>Available Groups</h3>
                  <input
                    type="text"
                    placeholder="Filter available groups..."
                    value={availableFilter}
                    onChange={(e) => setAvailableFilter(e.target.value)}
                    className="group-filter-input"
                  />
                  <div className="group-list-box">
                    {allAdGroups
                      .filter(group => !selectedGroups.find(sg => sg.full_ad_name === group.full_ad_name))
                      .filter(group => group.name.toLowerCase().includes(availableFilter.toLowerCase()))
                      .map((group, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => moveToSelected(group)}>
                          <div className="group-name">{group.name}</div>
                          <div className="group-desc">{group.full_ad_name}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
                <div className="group-arrows">
                  <div>↔</div>
                </div>
                <div className="group-column">
                  <h3>Selected Groups</h3>
                  <input
                    type="text"
                    placeholder="Filter selected groups..."
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="group-filter-input"
                  />
                  <div className="group-list-box">
                    {selectedGroups
                      .filter(group => group.name.toLowerCase().includes(selectedFilter.toLowerCase()))
                      .map((group, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => removeFromSelected(group)}>
                          <div className="group-name">{group.name}</div>
                          <div className="group-desc">{group.full_ad_name}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={createProfile}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the profile <strong>{profileToDelete?.name}</strong>?</p>
              <p className="text-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn-danger" onClick={deleteProfile}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showCreateByUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateByUserModal(false)}>
          <div className="modal-content group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Profile by User - Step {createByUserStep} of 3</h2>
              <button className="modal-close" onClick={() => setShowCreateByUserModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {createByUserStep === 1 && (
                <>
                  <div className="form-field">
                    <label className="form-label">Select User</label>
                    <input
                      type="text"
                      placeholder="Search users by username, name, or email..."
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="group-list-box" style={{maxHeight: '400px'}}>
                    {users
                      .filter(user => {
                        const search = userFilter.toLowerCase();
                        return user.username.toLowerCase().includes(search) ||
                               user.display_name.toLowerCase().includes(search) ||
                               user.email.toLowerCase().includes(search);
                      })
                      .map((user, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => selectUserForProfile(user)}>
                          <div className="group-name">{user.display_name} ({user.username})</div>
                          <div className="group-desc">{user.email}</div>
                        </div>
                      ))
                    }
                  </div>
                </>
              )}

              {createByUserStep === 2 && (
                <>
                  <div style={{marginBottom: '1rem'}}>
                    <strong>Selected User:</strong> {selectedUser?.display_name} ({selectedUser?.username})
                  </div>
                  <div className="form-field">
                    <label className="form-label">AD Groups (click to remove)</label>
                    <input
                      type="text"
                      placeholder="Filter groups..."
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                      className="group-filter-input"
                    />
                  </div>
                  <div className="group-list-box" style={{maxHeight: '300px'}}>
                    {selectedGroups
                      .filter(group => group.name.toLowerCase().includes(selectedFilter.toLowerCase()))
                      .map((group, idx) => (
                        <div key={idx} className="group-item-selectable" onClick={() => removeFromSelected(group)}>
                          <div className="group-name">{group.name}</div>
                          <div className="group-desc">{group.full_ad_name}</div>
                        </div>
                      ))
                    }
                  </div>
                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={() => setCreateByUserStep(1)}>Back</button>
                    <button className="btn-primary" onClick={() => setCreateByUserStep(3)}>Next</button>
                  </div>
                </>
              )}

              {createByUserStep === 3 && (
                <>
                  <div className="form-fields">
                    <div className="form-field">
                      <label className="form-label">Profile ID</label>
                      <input
                        type="text"
                        value={newProfile.profile_id}
                        onChange={(e) => setNewProfile({...newProfile, profile_id: e.target.value})}
                        className="form-input"
                        placeholder="Enter profile ID"
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        value={newProfile.name}
                        onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                        className="form-input"
                        placeholder="Enter profile name"
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Color</label>
                      <input
                        type="color"
                        value={newProfile.color}
                        onChange={(e) => setNewProfile({...newProfile, color: e.target.value})}
                        className="color-input"
                      />
                    </div>
                  </div>
                  <div style={{marginTop: '1rem'}}>
                    <strong>Selected Groups: {selectedGroups.length}</strong>
                  </div>
                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={() => setCreateByUserStep(2)}>Back</button>
                    <button className="btn-primary" onClick={createProfileFromUser}>Create Profile</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
